import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Select } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
    ButtonColors,
    ButtonModule,
    NumberfieldModule,
    TextfieldModule,
} from '@ed---interne/ng-uui-components';
import { ProjectsState } from '../../../project/project.state';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { HeaderService } from 'src/app/shared/services/header.service';
import { Observable } from 'rxjs';
import { DisplayType } from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import { DailyTimeEntryModel } from '../../timesheet.models';
import { User } from 'src/app/core/models/user.model';
import { TimesheetsState } from '../../timesheet.state';

@UntilDestroy()
@Component({
    selector: 'contributor-read',
    templateUrl: './contributor.component.html',
    styleUrls: ['./contributor.component.scss'],
    standalone: true,
    schemas: [],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        SharedModule,
        TextfieldModule,
        ButtonModule,
        NumberfieldModule,
        RouterModule,
    ],
})
export class ContributorComponent {
    public buttonColors = ButtonColors;

    @Select(TimesheetsState.contributorEntries)
    public contributorEntries$!: Observable<DailyTimeEntryModel[]>;
    public contributorEntries: DailyTimeEntryModel[] | undefined;

    @Select(TimesheetsState.contributor)
    public contributor$!: Observable<User>;
    public contributor: User | undefined;

    @Select(ProjectsState.isLoading)
    public isLoading$!: Observable<boolean>;
    public isLoading: boolean = true;

    public monthlyTimeData = new MatTableDataSource<{}>([]);
    public monthlyTimeColumns: any[] = [];

    public totalTimeData = new MatTableDataSource<{}>([]);
    public totalTimeColumns = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Projet',
            objectKey: 'projectLabel',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Phase',
            objectKey: 'phaseLabel',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Total',
            objectKey: 'total',
            bold: false,
        },
    ];

    constructor(
        private readonly router: Router,
        private readonly sidenavService: SidenavService,
        private readonly headerService: HeaderService,
    ) {
        this.isLoading$
            .pipe(untilDestroyed(this))
            .subscribe((isLoading) => (this.isLoading = isLoading));

        this.contributor$.pipe(untilDestroyed(this)).subscribe((contributor: User) => {
            if (!contributor) return;
            this.contributor = contributor;
            this.headerService.setBreadcrumbs([
                { label: 'Contributeur' },
                {
                    link: `/main/timesheets/contributor/${contributor.id}/`,
                    label: `${this.contributor.firstname} ${this.contributor.lastname}`,
                },
            ]);

            this.sidenavService.resetSubMenuItem('Feuilles de temps', [
                {
                    label: `Ma feuille de temps`,
                    icon: 'icon-clock-stopwatch',
                    routerLink: [`/main/timesheet/`],
                    routerLinkActiveOptions: { exact: true },
                    hidden: false,
                    isActive: false,
                    subItems: [],
                },
                {
                    label: `${this.contributor.firstname} ${this.contributor.lastname}`,
                    icon: 'icon-calendar',
                    routerLink: [`/main/timesheet/contributor/${this.contributor.id}`],
                    routerLinkActiveOptions: { exact: true },
                    hidden: false,
                    isActive: false,
                    subItems: [],
                },
            ]);
        });

        this.contributorEntries$
            .pipe(untilDestroyed(this))
            .subscribe((contributorEntries: DailyTimeEntryModel[]) => {
                if (!contributorEntries) return;
                this.contributorEntries = contributorEntries;
                this.generateMonthlyTimeData();
            });
    }

    public generateMonthlyTimeData() {
        if (!this.contributorEntries) return;
        let monthlyTimeColumns: any[] = [];
        const monthlyTimeData: any[] = [];
        const totalTimeData: any[] = [];
        for (const entry of this.contributorEntries) {
            const dateKey: string = `${new Date(entry.date).getFullYear()}-${String(new Date(entry.date).getMonth() + 1).padStart(2, '0')}`;

            if (!monthlyTimeColumns.find((m) => m.objectKey === dateKey)) {
                monthlyTimeColumns.push({
                    displayType: DisplayType.NUMBER,
                    objectDisplayName: dateKey,
                    objectKey: dateKey,
                    bold: false,
                });
            }
            const phase = entry?.phase;
            const project = phase?.project;
            if (!phase) continue;
            const exist = monthlyTimeData.find((u) => u.id === phase.id);
            const totalExist = totalTimeData.find((u) => u.id === phase.id);
            if (exist) {
                exist[dateKey] = (exist[dateKey] ?? 0) + entry.percentOfDay;
                exist.total += entry.percentOfDay;
                totalExist.total += entry.percentOfDay;
            } else {
                totalTimeData.push({
                    id: phase.id,
                    code: phase.code,
                    projectCode: project?.code,
                    projectLabel: `${phase.project?.label} `,
                    phaseLabel: `${phase.label} `,
                    [dateKey]: entry.percentOfDay || 0,
                    total: entry.percentOfDay || 0,
                });
                monthlyTimeData.push({
                    ...totalTimeData[totalTimeData.length - 1],
                    [dateKey]: entry.percentOfDay || 0,
                });
            }
        }

        monthlyTimeColumns = monthlyTimeColumns.sort((a, b) =>
            a.objectKey < b.objectKey ? -1 : 1,
        );
        this.monthlyTimeColumns = [...this.totalTimeColumns, ...monthlyTimeColumns];

        const totalLine = monthlyTimeData.reduce((acc, elem) => {
            Object.keys(elem).forEach((key) => {
                if (!isNaN(elem[key])) {
                    acc[key] = (acc[key] ?? 0) + elem[key];
                }
            });
            return acc;
        }, {});
        monthlyTimeData.push({
            id: null,
            code: null,
            projectCode: null,
            projectLabel: null,
            phaseLabel: `Total`,
            ...totalLine,
        });
        this.monthlyTimeData = new MatTableDataSource<{}>(monthlyTimeData);
        this.totalTimeData = new MatTableDataSource<{}>(totalTimeData);
    }

    public goToPhase(phaseId: any) {
        const data: any = this.monthlyTimeData.data.find((d: any) => d.id === phaseId);
        if (!data) return;

        this.router.navigate([`/main/projects/${data?.projectCode}/phase/${data?.code}`]);
    }
}
